import { Flex, Text } from "@chakra-ui/react";
import { t } from "i18next";
import { cloneDeep, isEqual } from "lodash";
import { useCallback } from "react";
import {
  DefaultNotificationSettingsDto,
  SourcingEventDateReminderDto,
} from "../../../autogen/bff-api";
import { useLoggedInWithOrgContextState } from "../../../common/auth/useLoggedInWithOrgContextState";
import { useApiError } from "../../../common/errors/useApiError";
import { useSaveOrganization } from "../../organizations/current/useOrganizationEditor";
import { DeleteSettingButton } from "./DeleteSettingButton";
import { initSettings } from "./NotificationSettings";
import { useReceiverRoleNames } from "./useReceiverRoleNames";

export const BidDeliveryDeadlineSetting = ({ setting }: { setting: SourcingEventDateReminderDto }) => {
  const roleName = useReceiverRoleNames();
  const authState = useLoggedInWithOrgContextState();
  const { trigger } = useApiError();

  const { saveOrganization, isSaving } = useSaveOrganization();

  const leadTimeUnits = {
    Days: t("days"),
    Months: t("months"),
  };

  const deleteSetting = useCallback(async () => {
    const settings: DefaultNotificationSettingsDto =
      cloneDeep(authState.selectedOrg.defaultNotificationSettings) ?? initSettings();
    settings.sourcingEventNotificationSettings.bidDeliveryDeadlineNotifications =
      settings.sourcingEventNotificationSettings.bidDeliveryDeadlineNotifications.filter((s) => !isEqual(s, setting));
    try {
      await saveOrganization({ defaultNotificationSettings: settings });
    } catch (e) {
      trigger(e);
    }
  }, [authState.selectedOrg.defaultNotificationSettings, trigger, setting, saveOrganization]);

  return (
    <Flex flexDirection={"column"} fontSize={"sm"} backgroundColor={"gray.50"} rounded="md" p="2" my="1">
      <Flex justifyContent={"space-between"} width="full">
        <Text>{t("Time of notification")}</Text>
        <DeleteSettingButton isLoading={isSaving} onClick={() => deleteSetting()} />
      </Flex>
      <Text fontWeight={"bold"}>
        {setting.leadTime.amount} {leadTimeUnits[setting.leadTime.unit]} {t("before date")}
      </Text>
      <Text>{t("Individual receivers")}</Text>
      {setting.receivers.persons.map((person) => (
        <Text key={person.id} fontWeight={"bold"}>
          {person.firstName} {person.lastName}
        </Text>
      ))}
      <Text>{t("Organization role receivers")}</Text>
      {setting.receivers.organizationRoles.map((role) => (
        <Text key={role} fontWeight={"bold"}>
          {roleName.organizationRole(role)}
        </Text>
      ))}
      <Text>{t("Procurement role receivers")}</Text>
      {setting.receivers.sourcingEventRoles.map((role) => (
        <Text key={role} fontWeight={"bold"}>
          {roleName.sourcingEventRole(role)}
        </Text>
      ))}
    </Flex>
  );
};
